import { useState } from "react";
import { CalendarClock, Loader2, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useStartRenewedCycleNowMutation } from "@/generated/graphql";
import { useToast } from "@/hooks/use-toast";
import { refetchAllMyData } from "@/lib/apollo/refetchAllMyData";
import {
  pendingCycleResetAt,
  type PendingCycleResetSubscriptionLike,
} from "@/lib/billing/pendingCycleResetAt";
import { cn } from "@/lib/utils";

interface RenewedCycleNoticeProps {
  subscription: PendingCycleResetSubscriptionLike | null | undefined;
  /** Called once the new cycle has started (every `myData` was refetched). */
  onStarted?: () => void;
  className?: string;
  /** `compact`: only the start-now button (the Sidebar plan card already shows the date). */
  variant?: "full" | "compact";
}

/**
 * After a PIX renewal paid early, the current limits keep running until the
 * old due date. This tells the customer so and lets them start the new cycle
 * now, after confirming that what is left of the current one is dropped.
 * Renders nothing unless `pending_cycle_reset_at` is still ahead.
 *
 * After the start, every `myData` observer is refetched. `no_pending_cycle`
 * counts as already started, unless that refetch still shows a future reset:
 * then nothing started, and the error dialog says so.
 */
export function RenewedCycleNotice({ subscription, onStarted, className, variant = "full" }: RenewedCycleNoticeProps) {
  const resetAt = pendingCycleResetAt(subscription, new Date());
  if (!resetAt) return null;
  return <PendingCycleNotice resetAt={resetAt} onStarted={onStarted} className={className} variant={variant} />;
}

function PendingCycleNotice({
  resetAt,
  onStarted,
  className,
  variant,
}: { resetAt: Date } & Pick<RenewedCycleNoticeProps, "onStarted" | "className" | "variant">) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [startRenewedCycleNow] = useStartRenewedCycleNowMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const language = i18n?.language;
  // The backend runs the deferred reset on this instant: show its São Paulo day.
  const date = resetAt.toLocaleDateString(language && !language.startsWith("pt") ? "en-US" : "pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "America/Sao_Paulo",
  });

  const handleConfirm = async () => {
    setStarting(true);
    let failed = false;
    let alreadyStarted = false;
    try {
      await startRenewedCycleNow();
      toast({ title: t("billing.renewedCycle.started") });
    } catch (error) {
      // no_pending_cycle: the new cycle already started (due date reached, or
      // started elsewhere), so there is nothing left to do but refresh.
      if (error instanceof Error && error.message.includes("no_pending_cycle")) {
        alreadyStarted = true;
      } else {
        console.error("Error starting the renewed cycle:", error);
        failed = true;
      }
    }
    if (!failed) {
      // The Sidebar, the page and the modal each hold their own myData.
      const results = await Promise.resolve(refetchAllMyData()).catch((error) => {
        console.error("Error refreshing after the renewed cycle started:", error);
        return [];
      });
      // Told nothing was pending, yet the reset is still ahead: nothing started.
      failed = alreadyStarted && results.some((result) => pendingCycleResetAt(
        (result?.data as { myData?: { subscription?: PendingCycleResetSubscriptionLike | null } } | undefined)
          ?.myData?.subscription,
        new Date(),
      ) !== null);
    }
    setStarting(false);
    setConfirmOpen(false);
    if (failed) {
      setErrorMessage(t("billing.renewedCycle.error"));
      return;
    }
    // Hosts may refresh more (wallet, parent): a failed refresh must not go unhandled.
    void Promise.resolve()
      .then(() => onStarted?.())
      .catch((error) => console.error("Error refreshing after the renewed cycle started:", error));
  };

  return (
    <>
      {variant === "compact" ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="renewed-cycle-notice"
          className={cn("w-full", className)}
          onClick={() => setConfirmOpen(true)}
        >
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          {t("billing.renewedCycle.startNow")}
        </Button>
      ) : (
        <div
          data-testid="renewed-cycle-notice"
          role="status"
          aria-live="polite"
          className={cn("space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-left text-sm", className)}
        >
          <p className="flex items-start gap-2">
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <span>{t("billing.renewedCycle.notice", { date })}</span>
          </p>
          <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => setConfirmOpen(true)}>
            {t("billing.renewedCycle.startNow")}
          </Button>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={(open) => { if (!starting) setConfirmOpen(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("billing.renewedCycle.confirmTitle")}</DialogTitle>
            <DialogDescription>{t("billing.renewedCycle.confirmDescription")}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)} disabled={starting}>
              {t("common.cancel")}
            </Button>
            <Button type="button" onClick={handleConfirm} disabled={starting}>
              {starting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("billing.renewedCycle.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("common.error")}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}
